export const PAGE_SIZE = 15;

export const AIOPS_API_BASE_URL =
  process.env.REACT_APP_AIOPS_API_BASE_URL || "";
export const AUTH_STORAGE_KEY = "aiops_auth";
export const TOKEN_STORAGE_KEY = "aiops_token";

function findTokenLikeValue(value) {
  if (!value || typeof value !== "object") return null;

  for (const key of ["access_token", "token", "jwt", "id_token"]) {
    if (typeof value[key] === "string" && value[key]) return value[key];
  }

  for (const nestedValue of Object.values(value)) {
    const token = findTokenLikeValue(nestedValue);
    if (token) return token;
  }

  return null;
}

async function readJsonResponse(res) {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getErrorMessage(data, fallback) {
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  return fallback;
}

function redirectToLogin() {
  if (window.location.pathname !== "/login" && window.location.pathname !== "/admin") {
    window.location.assign("/login");
  }
}

export function getStoredAuth() {
  const rawAuth = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!rawAuth) return null;

  try {
    const authResponse = JSON.parse(rawAuth);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) || findTokenLikeValue(authResponse);

    if (!token) {
      clearAuth();
      return null;
    }

    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    return authResponse;
  } catch {
    clearAuth();
    return null;
  }
}

export function storeAuth(authResponse) {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authResponse));

  const token = findTokenLikeValue(authResponse);
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export function clearAuth() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export async function login(email, password) {
  const res = await fetch(`${AIOPS_API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      password,
    }),
  });

  const data = await readJsonResponse(res);

  if (!res.ok) {
    throw new Error(getErrorMessage(data, "Login failed"));
  }

  const authResponse = data ?? {};
  storeAuth(authResponse);
  return authResponse;
}

function decodeJwt(token) {
  try {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export function getOrgId() {
  // 1. Try to extract org_id from JWT token payload directly
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    const payload = decodeJwt(token);
    if (payload) {
      if (payload.org_id) return payload.org_id;
      if (payload.organization_id) return payload.organization_id;
      if (payload.tenant_id) return payload.tenant_id;
    }
  }

  // 2. Try to extract from stored authentication response
  const auth = getStoredAuth();
  if (auth) {
    if (auth.org_id) return auth.org_id;
    if (auth.user?.org_id) return auth.user.org_id;
    if (auth.organization_id) return auth.organization_id;
    if (Array.isArray(auth.organizations) && auth.organizations[0]?.id) return auth.organizations[0].id;
    if (Array.isArray(auth.user?.organizations) && auth.user.organizations[0]?.id) return auth.user.organizations[0].id;
  }
  return process.env.REACT_APP_ORG_ID || "e0114002-3c22-482a-a92e-336c20509a2e";
}

export async function api(path, options = {}) {
  const { headers, ...fetchOptions } = options;
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);

  if (!token) {
    clearAuth();
    redirectToLogin();
    throw new Error("Authentication required");
  }

  const formattedPath = path.startsWith("/") ? path : `/${path}`;

  const res = await fetch(`${AIOPS_API_BASE_URL}${formattedPath}`, {
    mode: "cors",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Org-ID": getOrgId(),
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    ...fetchOptions,
  });

  const data = await readJsonResponse(res);

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      clearAuth();
      redirectToLogin();
    }

    throw new Error(getErrorMessage(data, `API error ${res.status}`));
  }

  return data;
}
